import type { ComponentType, SVGProps } from 'react';
import { Category, RefProvider, SubIdExperienceVariant } from './enums';
import { globalConfig } from './client';
import { VendorApp } from './vendors';
import AiStoreBuilderLogo from 'assets/svgs/ai-store-builder.svg';
import ZenDropLogo from 'assets/svgs/zendrop-logo.svg';

export type TemplateLangMap = { en: string } & Record<string, string>;
export type CategoryTemplateMap = Partial<Record<Category, TemplateLangMap>>;
type SvgComponent = ComponentType<SVGProps<SVGSVGElement>>;

export type ProviderTerms = {
  /** i18n translation keys whose values resolve to URLs */
  linkKeys: Record<string, string>;
};

export interface SubIdConfig {
  categoryOverride?: Category;
  showWelcome?: boolean;
  experienceVariant?: SubIdExperienceVariant;
  productCount?: number;
  successUrl?: string | null;
}

export type ProviderConfig = {
  vendor: VendorApp;
  templates: CategoryTemplateMap;
  terms: ProviderTerms;
  skipWelcomeStep?: boolean;
  logo?: SvgComponent;
  successUrl?: string;
  dashboardUrl?: string;
  brandName?: string;
  subIdOverrides?: Record<string, SubIdConfig>;
};

// ─── Templates ───────────────────────────────────────────────────────────────

const storeBuilderTemplates: CategoryTemplateMap = {
  [Category.WomensFashion]: {
    // slug 2514
    en: '25529185-9805-4bfe-9721-0e0e55b7eafd',
    fr: '1ea95a91-721e-4057-af98-ff0db1a69afd',
    de: '97b41125-114e-4d1f-9002-82116b8ec096',
    ja: '1cad70a0-2b88-44be-872d-a6c92fa029d1',
    es: '6e05b9b0-f6d5-451d-a2b8-987df3272a26',
    it: 'ca11886c-1135-4156-b799-ca8b8b7442b9',
  },
  [Category.MensFashion]: {
    // slug 2936
    en: 'fae66f4e-4fe8-4ae5-8cb1-d038728c1ace',
    fr: '0d2fb94c-9082-4a0e-9829-31053bc8b76d',
    de: 'd74e8fd5-924e-47f7-ab48-e81dc41e1761',
    ja: '1d68e4f5-09fa-465d-b626-6aaf33d66f24',
    es: '7848c7f6-4daf-456a-a7c9-d98638f58f21',
    it: '95d436ed-f938-403d-9970-68ac2900e362',
  },
  [Category.JewelryAccessories]: {
    // slug 1615
    en: '29ffdf7e-b9e7-4143-bea9-8d95755583e1',
    fr: '7221e90b-e078-4450-a9d8-5ea366d487fc',
    de: '6a71350b-5b86-433d-a940-9ced85ae6b6f',
    ja: '3a878f6e-1101-4144-bec1-eb9ab3ba303e',
    es: '4d89dad3-bc90-4464-8119-d79840852942',
    it: '0a2ea4fb-b93a-4e1a-825d-bbf3b87d0a53',
  },
  [Category.KidsBabies]: {
    // slug 2058
    en: '7528333c-96c8-45da-a4ff-5d19e334b4af',
    fr: 'bf8ab3c7-7506-41ac-929f-7ece7bdfb878',
    de: '99dd8799-c66f-4dce-8acd-77744d955a77',
    ja: 'e410e7dd-f2dd-47d2-b3f6-c72731d32f94',
    es: 'a833716a-6dfa-4642-85fa-3b7a3d453143',
    it: 'bc5e01d3-6e5f-426f-bfc3-ca558d22572e',
  },
  [Category.BeautyHealth]: {
    // slug 2940
    en: '9b8cfd1c-c099-40c5-8ea9-c87fa30f99a1',
    fr: '1dde53cf-acb9-4d56-b0d6-59fa98d488eb',
    de: 'f8754940-a182-4fd6-ab05-a3aa45c13e37',
    ja: '2f3b761e-0d9a-4bbc-848e-5bb1a470872b',
    es: '2d14e7c4-b9f1-4193-a657-caaeed1d0343',
    it: '049a840e-a63f-4624-8289-ac714cfeb2ec',
  },
  [Category.HomeGarden]: {
    // slug 2745
    en: 'da7dd4f7-54cf-47a7-8752-b76f99e439b6',
    fr: '27889244-a492-427e-93c4-b9aee2299735',
    de: 'aba6141d-2052-45c8-8300-e7640aea0117',
    ja: 'a7a6c5fa-1bb6-48d6-8188-a6d02abe16ec',
    es: '4daa892f-9e6f-4eac-a454-d17b92684a44',
    it: '7efa4a9a-edc1-4409-87ab-59022aa429a3',
  },
  [Category.SportsEntertainment]: {
    // slug 2685
    en: '8fc69242-47c2-4bd3-aedd-2fdea4e2c2a6',
    fr: 'b701ba85-4cb1-4496-8262-207ca10d1145',
    de: '1af235b3-19a0-4d30-9f59-37230fe6eefc',
    ja: 'b701ba85-4cb1-4496-8262-207ca10d1145',
    es: 'b701ba85-4cb1-4496-8262-207ca10d1145',
    it: 'b701ba85-4cb1-4496-8262-207ca10d1145',
  },
  [Category.BagsLuggage]: {
    // slug 1762
    en: '8ccb4934-0cda-48a1-bed8-4d2b3ab617a7',
    fr: '8a17eff5-aba8-4e93-8315-07b6dfe85465',
    de: '5579def6-b9f9-47ea-92b2-81689a12897c',
    ja: '83deb44b-ea56-4aa0-9876-d6bf519ade7a',
    es: 'b157bca2-e169-40b3-af4f-5d680ac3a9b2',
    it: 'e63c8261-1beb-4131-a5f2-b64222d95690',
  },
  [Category.ConsumerElectronics]: {
    // slug 2860
    en: '4052c015-c49c-4656-a5d3-aee3929c9156',
    fr: 'aace2603-c979-4f18-9b1c-4a93273d4a57',
    de: '969d5527-fa82-455b-bfdc-2cab95c307a8',
    ja: '50fedd35-96fe-47d7-aa48-46a5a24239fe',
    es: 'c070ce34-7e3a-425a-b413-eb0456944fd9',
    it: 'b13ae6bf-ed09-484d-a708-707c0bd807e7',
  },
  [Category.Pets]: {
    // slug 2331
    en: '692a105f-c10d-4cc2-9bd6-3df9ca3efb5a',
    fr: '7bdf7d82-76e4-4ccf-b02f-3ce5b5980f7c',
    de: '7fceb69f-33be-43c6-90f1-bb5c80fa0339',
    ja: '5ca67c21-1175-458c-b54f-1882ca593def',
    es: 'a80260b0-25fc-4692-bcbf-a0e9cbd1873b',
    it: '692a105f-c10d-4cc2-9bd6-3df9ca3efb5a',
  },
};

const zenDropTemplates: CategoryTemplateMap = {
  [Category.WomensFashion]: {
    en: 'ca11886c-1135-4156-b799-ca8b8b7442b9',
    pt: '3622e7f5-9de7-44e7-b993-55c0b8f7c088',
    fr: '1ea95a91-721e-4057-af98-ff0db1a69afd',
    de: '97b41125-114e-4d1f-9002-82116b8ec096',
    ja: '1cad70a0-2b88-44be-872d-a6c92fa029d1',
    es: '6e05b9b0-f6d5-451d-a2b8-987df3272a26',
    it: 'ca11886c-1135-4156-b799-ca8b8b7442b9',
  },
  [Category.MensFashion]: {
    en: 'd74e8fd5-924e-47f7-ab48-e81dc41e1761',
    pt: '3d57bc33-2b3b-49f2-b66e-eabf510092d9',
    fr: '0d2fb94c-9082-4a0e-9829-31053bc8b76d',
    de: 'd74e8fd5-924e-47f7-ab48-e81dc41e1761',
    ja: '1d68e4f5-09fa-465d-b626-6aaf33d66f24',
    es: '7848c7f6-4daf-456a-a7c9-d98638f58f21',
    it: '95d436ed-f938-403d-9970-68ac2900e362',
  },
  [Category.JewelryAccessories]: {
    en: '0a2ea4fb-b93a-4e1a-825d-bbf3b87d0a53',
    pt: '44eee06f-9d15-4b3f-9c0e-f7c3ebdf9575',
    fr: '7221e90b-e078-4450-a9d8-5ea366d487fc',
    de: '6a71350b-5b86-433d-a940-9ced85ae6b6f',
    ja: '3a878f6e-1101-4144-bec1-eb9ab3ba303e',
    es: '4d89dad3-bc90-4464-8119-d79840852942',
    it: '0a2ea4fb-b93a-4e1a-825d-bbf3b87d0a53',
  },
  [Category.KidsBabies]: {
    en: 'bc5e01d3-6e5f-426f-bfc3-ca558d22572e',
    pt: '56bb8b34-2426-4461-a8f8-cae47ef57120',
    fr: 'bf8ab3c7-7506-41ac-929f-7ece7bdfb878',
    de: '99dd8799-c66f-4dce-8acd-77744d955a77',
    ja: 'e410e7dd-f2dd-47d2-b3f6-c72731d32f94',
    es: 'a833716a-6dfa-4642-85fa-3b7a3d453143',
    it: 'bc5e01d3-6e5f-426f-bfc3-ca558d22572e',
  },
  [Category.BeautyHealth]: {
    en: '049a840e-a63f-4624-8289-ac714cfeb2ec',
    pt: '44c2f891-5c61-4f2a-8bc3-363e0e31720a',
    fr: '1dde53cf-acb9-4d56-b0d6-59fa98d488eb',
    de: 'f8754940-a182-4fd6-ab05-a3aa45c13e37',
    ja: '2f3b761e-0d9a-4bbc-848e-5bb1a470872b',
    es: '2d14e7c4-b9f1-4193-a657-caaeed1d0343',
    it: '049a840e-a63f-4624-8289-ac714cfeb2ec',
  },
  [Category.HomeGarden]: {
    en: '7efa4a9a-edc1-4409-87ab-59022aa429a3',
    pt: '96572b0c-915e-44c7-8bfa-db9a760a9146',
    fr: '27889244-a492-427e-93c4-b9aee2299735',
    de: 'aba6141d-2052-45c8-8300-e7640aea0117',
    ja: 'a7a6c5fa-1bb6-48d6-8188-a6d02abe16ec',
    es: '4daa892f-9e6f-4eac-a454-d17b92684a44',
    it: '7efa4a9a-edc1-4409-87ab-59022aa429a3',
  },
  [Category.SportsEntertainment]: {
    en: 'b701ba85-4cb1-4496-8262-207ca10d1145',
    pt: '3e1ab8e6-ba78-464c-b35a-450422b13fa9',
    fr: 'b701ba85-4cb1-4496-8262-207ca10d1145',
    de: '1af235b3-19a0-4d30-9f59-37230fe6eefc',
    ja: 'b701ba85-4cb1-4496-8262-207ca10d1145',
    es: 'b701ba85-4cb1-4496-8262-207ca10d1145',
    it: 'b701ba85-4cb1-4496-8262-207ca10d1145',
  },
  [Category.BagsLuggage]: {
    en: 'e63c8261-1beb-4131-a5f2-b64222d95690',
    pt: 'f4676e63-cf66-463f-81e3-8e3baa443f70',
    fr: '8a17eff5-aba8-4e93-8315-07b6dfe85465',
    de: '5579def6-b9f9-47ea-92b2-81689a12897c',
    ja: '83deb44b-ea56-4aa0-9876-d6bf519ade7a',
    es: 'b157bca2-e169-40b3-af4f-5d680ac3a9b2',
    it: 'e63c8261-1beb-4131-a5f2-b64222d95690',
  },
  [Category.ConsumerElectronics]: {
    en: 'b13ae6bf-ed09-484d-a708-707c0bd807e7',
    pt: 'eb599f69-48f6-4e5b-af46-522ab193fb15',
    fr: 'aace2603-c979-4f18-9b1c-4a93273d4a57',
    de: '969d5527-fa82-455b-bfdc-2cab95c307a8',
    ja: '50fedd35-96fe-47d7-aa48-46a5a24239fe',
    es: 'c070ce34-7e3a-425a-b413-eb0456944fd9',
    it: 'b13ae6bf-ed09-484d-a708-707c0bd807e7',
  },
  [Category.Pets]: {
    en: '692a105f-c10d-4cc2-9bd6-3df9ca3efb5a',
    pt: 'b583ac9f-123a-4889-bac9-86e2c5004e27',
    fr: '7bdf7d82-76e4-4ccf-b02f-3ce5b5980f7c',
    de: '7fceb69f-33be-43c6-90f1-bb5c80fa0339',
    ja: '5ca67c21-1175-458c-b54f-1882ca593def',
    es: 'a80260b0-25fc-4692-bcbf-a0e9cbd1873b',
    it: '692a105f-c10d-4cc2-9bd6-3df9ca3efb5a',
  },
};

const autoDsTemplates: CategoryTemplateMap = {
  [Category.WomensFashion]: { en: '4a339fee-d4a5-4d71-9dc2-415a6d3144ef' },
  [Category.ConsumerElectronics]: { en: '46a77abd-70c8-434f-8b0e-a023e529ee3b' },
  [Category.HomeGarden]: { en: 'f4dc5c01-fabe-4244-acd4-64f963b2180e' },
  [Category.Pets]: { en: 'f4dc5c01-fabe-4244-acd4-64f963b2180e' },
  [Category.SportsEntertainment]: { en: '11ec0b4b-d971-42e9-83e8-1b9b9799c49f' },
};

// ─── Terms ───────────────────────────────────────────────────────────────────

const modalystTerms: ProviderTerms = {
  linkKeys: {
    appMarketLink: 'links.app_market_terms',
    modalystLink: 'links.modalyst_terms',
    modalystPrivacyLink: 'links.modalyst_privacy',
  },
};

const zendropTerms: ProviderTerms = {
  linkKeys: {
    appMarketLink: 'links.app_market_terms',
    zendropLink: 'links.zendrop_terms',
    zendropPrivacyLink: 'links.zendrop_privacy',
  },
};

const autoDsTerms: ProviderTerms = {
  linkKeys: {
    appMarketLink: 'links.app_market_terms',
    autodsLink: 'links.autods_terms',
  },
};

// ─── Provider config ──────────────────────────────────────────────────────────

export const PROVIDER_CONFIG: Record<RefProvider, ProviderConfig> = {
  [RefProvider.Standalone]: {
    vendor: VendorApp.Modalyst,
    templates: storeBuilderTemplates,
    terms: modalystTerms,
  },
  [RefProvider.StoreBuilder]: {
    vendor: VendorApp.Modalyst,
    successUrl: globalConfig.config.aiStoreBuilderSuccessUrl,
    logo: AiStoreBuilderLogo as SvgComponent,
    brandName: 'AiStoreBuilder',
    templates: storeBuilderTemplates,
    subIdOverrides: {
      imperiumacquisition: {
        successUrl: null,
      },
      iosapp: {
        successUrl: 'https://ios.aistorebuilder.com/wix/success',
      },
    },
    terms: modalystTerms,
  },
  [RefProvider.ZenDrop]: {
    vendor: VendorApp.ZenDrop,
    successUrl: globalConfig.config.zenDropSuccessUrl,
    dashboardUrl: globalConfig.config.zenDropDashboardUrl,
    logo: ZenDropLogo as SvgComponent,
    brandName: 'Zendrop',
    skipWelcomeStep: true,
    templates: zenDropTemplates,
    subIdOverrides: {
      zd_expert: { categoryOverride: Category.ConsumerElectronics },
      '3575575': {
        showWelcome: true,
        experienceVariant: SubIdExperienceVariant.Morrison,
        productCount: 100,
      },
      'loja-vencedora': {
        showWelcome: true,
        experienceVariant: SubIdExperienceVariant.Vencedora,
      },
    },
    terms: zendropTerms,
  },
  [RefProvider.AutoDS]: {
    vendor: VendorApp.AutoDs,
    successUrl: globalConfig.config.autodsSuccessUrl,
    templates: autoDsTemplates,
    terms: autoDsTerms,
  },
  [RefProvider.Batistella]: {
    vendor: VendorApp.Modalyst,
    successUrl: globalConfig.config.batistellaSuccessUrl,
    templates: storeBuilderTemplates,
    terms: modalystTerms,
  },
};

// ─── Provider resolution ──────────────────────────────────────────────────────

export function getRefProvider(): RefProvider {
  const raw = globalConfig.initialState.refProvider;
  return (raw as RefProvider | undefined) ?? RefProvider.Standalone;
}

export function getProviderConfig(): ProviderConfig {
  const provider = getRefProvider();
  return PROVIDER_CONFIG[provider];
}

export function getSubIdConfig(): SubIdConfig | undefined {
  const subId = globalConfig.initialState.subId;
  const subIdOverrides = getProviderConfig().subIdOverrides;
  if (!subId || !subIdOverrides || !Object.prototype.hasOwnProperty.call(subIdOverrides, subId)) {
    return undefined;
  }

  return subIdOverrides[subId];
}

export function getSuccessUrl(): string | undefined {
  const subIdSuccessUrl = getSubIdConfig()?.successUrl;
  if (subIdSuccessUrl === null) {
    return undefined;
  }

  return subIdSuccessUrl ?? getProviderConfig().successUrl;
}

export function getProductCount(): number {
  return getSubIdConfig()?.productCount ?? 20;
}

export function getCategoriesForProvider(refProvider: RefProvider): readonly Category[] {
  return Object.keys(PROVIDER_CONFIG[refProvider].templates) as Category[];
}

export function getCategoriesForCurrentProvider(): Category[] {
  return [...getCategoriesForProvider(getRefProvider())];
}
